import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, Phone, Mail, Trash2, CheckCircle2, RotateCcw,
  RefreshCw, ShieldCheck, Inbox, BellRing, MapPin, Check, ExternalLink,
  Star, Send, MessageCircle, Newspaper,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GBP_STEPS = [
  "Go to google.com/business and sign in with your Google account",
  "Add business name \"PocketBima\" and choose the category \"Insurance agency\"",
  "Add your office address — N-303, Stellar Jeevan, Sector 1, Greater Noida West — and complete verification",
  "Add phone 9598063001, your website link and WhatsApp number",
  "Upload your logo, office photos and photos from this website",
  "List your services: Motor, Health, Life & Term, Business and Travel insurance",
  "Ask happy customers for Google reviews — and reply to every one",
  "Post a photo or update every week to stay active in local search",
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function daysLeft(dateStr) {
  const diff = new Date(dateStr + "T00:00:00") - new Date(new Date().toDateString());
  return Math.round(diff / 86400000);
}

const inputCls =
  "w-full rounded-xl border border-navy/10 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-charcoal/40 outline-none transition-colors duration-200 focus:border-mint focus:ring-2 focus:ring-mint/20";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("pb_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [gbpDone, setGbpDone] = useState(() => JSON.parse(localStorage.getItem("pb_gbp_steps") || "[]"));
  const [settings, setSettings] = useState(null);
  const [reviewLink, setReviewLink] = useState("");
  const [reviewName, setReviewName] = useState("");
  const [reviewPhone, setReviewPhone] = useState("");
  const [reviewRequests, setReviewRequests] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("pb_admin_token");
    setToken(null);
    setEnquiries(null);
    setReminders(null);
  }, []);

  const load = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [eq, rm, st, rr] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers }),
        axios.get(`${API}/admin/reminders`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
        axios.get(`${API}/admin/review-requests`, { headers }),
      ]);
      setEnquiries(eq.data);
      setReminders(rm.data);
      setSettings(st.data);
      setReviewLink(st.data.google_review_link);
      setReviewRequests(rr.data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("pb_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "contacted" ? "Marked as contacted" : "Moved back to new");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((list) => list.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeReminder = async (id) => {
    if (!window.confirm("Delete this reminder permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reminders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReminders((list) => list.filter((x) => x.id !== id));
      toast.success("Reminder deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setReminderStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/reminders/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReminders((list) => list.map((x) => (x.id === id ? { ...x, status: data.status } : x)));
      toast.success(status === "handled" ? "Marked as handled — nice win-back!" : "Moved back to pending");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleGbp = (i) => {
    setGbpDone((done) => {
      const next = done.includes(i) ? done.filter((x) => x !== i) : [...done, i];
      localStorage.setItem("pb_gbp_steps", JSON.stringify(next));
      return next;
    });
  };

  const saveLink = async () => {
    setLinkSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/settings`,
        { google_review_link: reviewLink },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings((s) => ({ ...s, google_review_link: data.google_review_link }));
      toast.success("Review link saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLinkSaving(false);
    }
  };

  const sendReview = async (channel) => {
    if (!reviewName.trim() || !reviewPhone.trim()) {
      toast.error("Enter the customer's name and mobile number");
      return;
    }
    setReviewLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/review-requests`,
        { name: reviewName.trim(), phone: reviewPhone.trim(), channel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (channel === "whatsapp") {
        window.open(
          `https://wa.me/91${reviewPhone.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(data.message)}`,
          "_blank"
        );
      }
      toast.success(channel === "sms" ? "SMS sent to customer!" : "WhatsApp chat opened — just hit send");
      setReviewRequests((l) => [data.record, ...(l || [])]);
      setReviewName("");
      setReviewPhone("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReviewLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="hero-grid-bg bg-ice py-24" data-testid="admin-login-page">
        <div className="mx-auto max-w-md px-5">
          <div className="rounded-[2rem] border border-navy/10 bg-white p-8 shadow-soft sm:p-10">
            <div className="flex justify-center">
              <img src="/images/logo.webp" alt="Pocket Bima Insurance Service" width="800" height="667" className="h-24 w-auto" />
            </div>
            <h1 className="mt-6 text-center font-display text-2xl font-extrabold tracking-tight text-navy">
              Enquiry Dashboard
            </h1>
            <p className="mt-2 text-center text-sm text-charcoal/70">
              Private area. Sign in to view quote enquiries.
            </p>
            <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
              <input
                type="email" required placeholder="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} className={inputCls}
                data-testid="admin-email-input" aria-label="Email"
              />
              <input
                type="password" required placeholder="Password" value={password}
                onChange={(e) => setPassword(e.target.value)} className={inputCls}
                data-testid="admin-password-input" aria-label="Password"
              />
              {error && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <button
                type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-mint py-4 text-base font-bold text-white shadow-mint transition-all duration-200 hover:-translate-y-0.5 hover:bg-mint-600 disabled:opacity-60"
                data-testid="admin-login-submit"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  const total = enquiries?.length ?? 0;
  const fresh = enquiries?.filter((x) => x.status !== "contacted").length ?? 0;

  return (
    <main className="min-h-[70vh] bg-ice py-16" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy" data-testid="admin-dashboard-heading">
              Enquiry Dashboard
            </h1>
            <p className="mt-1 text-sm text-charcoal/70">All quote requests from the website, newest first.</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/blog"
              className="flex items-center gap-2 rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:border-mint hover:text-mint-600"
              data-testid="admin-blog-link"
            >
              <Newspaper className="h-4 w-4" /> Blog
            </Link>
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:border-mint hover:text-mint-600"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-4">
          {[
            { label: "Total", value: total, testid: "stat-total" },
            { label: "New", value: fresh, testid: "stat-new" },
            { label: "Contacted", value: total - fresh, testid: "stat-contacted" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-navy/10 bg-white p-5 text-center shadow-soft" data-testid={s.testid}>
              <p className="font-display text-3xl font-extrabold text-navy">{s.value}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {enquiries === null && (
            <div className="flex justify-center py-16" data-testid="admin-loading">
              <Loader2 className="h-8 w-8 animate-spin text-mint" />
            </div>
          )}
          {enquiries?.length === 0 && (
            <div className="rounded-3xl border border-dashed border-navy/20 bg-white p-16 text-center" data-testid="admin-empty">
              <Inbox className="mx-auto h-10 w-10 text-charcoal/30" />
              <p className="mt-4 font-semibold text-charcoal/60">No enquiries yet.</p>
            </div>
          )}
          {enquiries?.map((e) => (
            <div
              key={e.id}
              className="rounded-3xl border border-navy/10 bg-white p-6 shadow-soft sm:p-7"
              data-testid={`enquiry-card-${e.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-lg font-bold text-navy" data-testid="enquiry-name">{e.name}</h3>
                    <span className="rounded-full bg-skycyan/10 px-3 py-1 text-xs font-bold text-skycyan-600" data-testid="enquiry-type">
                      {e.insurance_type}
                    </span>
                    {e.source === "chat" && (
                      <span className="rounded-full bg-navy/10 px-3 py-1 text-xs font-bold text-navy" data-testid="enquiry-source">
                        AI Chat
                      </span>
                    )}
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${
                        e.status === "contacted" ? "bg-mint-50 text-mint-700" : "bg-amber-50 text-amber-600"
                      }`}
                      data-testid="enquiry-status"
                    >
                      {e.status === "contacted" ? "Contacted" : "New"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-charcoal/50" data-testid="enquiry-date">
                    {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    {" · "}{e.city}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a href={`tel:+91${e.phone.replace(/\D/g, "").slice(-10)}`} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-mint hover:text-mint-600" data-testid="enquiry-call">
                    <Phone className="h-3.5 w-3.5" /> {e.phone}
                  </a>
                  <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-mint hover:text-mint-600" data-testid="enquiry-email">
                    <Mail className="h-3.5 w-3.5" /> Email
                  </a>
                  {e.status === "contacted" ? (
                    <button onClick={() => setStatus(e.id, "new")} className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-navy-700" data-testid="enquiry-mark-new">
                      <RotateCcw className="h-3.5 w-3.5" /> Mark New
                    </button>
                  ) : (
                    <button onClick={() => setStatus(e.id, "contacted")} className="flex items-center gap-1.5 rounded-full bg-mint px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-mint-600" data-testid="enquiry-mark-contacted">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark Contacted
                    </button>
                  )}
                  <button onClick={() => remove(e.id)} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-50" data-testid="enquiry-delete">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
              {e.message && (
                <p className="mt-4 rounded-2xl bg-ice px-5 py-3.5 text-sm leading-relaxed text-charcoal/85" data-testid="enquiry-message">
                  {e.message}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 grid items-start gap-8 lg:grid-cols-3">
          {/* RENEWAL REMINDERS */}
          <section data-testid="reminders-section">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy">
                <BellRing className="h-5 w-5 text-mint" />
              </span>
              <div>
                <h2 className="font-display text-xl font-extrabold tracking-tight text-navy" data-testid="reminders-heading">
                  Renewal Reminders
                </h2>
                <p className="text-xs text-charcoal/60">Sorted by expiry date — call back before renewal is due.</p>
              </div>
              <span className="ml-auto rounded-full bg-mint-50 px-3 py-1 text-xs font-bold text-mint-700" data-testid="reminders-count">
                {reminders?.length ?? 0}
              </span>
            </div>
            <div className="mt-5 space-y-3">
              {reminders?.length === 0 && (
                <div className="rounded-2xl border border-dashed border-navy/20 bg-white p-10 text-center" data-testid="reminders-empty">
                  <p className="text-sm font-semibold text-charcoal/60">No renewal reminders yet.</p>
                </div>
              )}
              {reminders?.map((r) => {
                const d = daysLeft(r.expiry_date);
                const overdue = d < 0;
                const handled = r.status === "handled";
                return (
                  <div
                    key={r.id}
                    className={`rounded-2xl border p-5 shadow-soft transition-all duration-200 ${
                      handled
                        ? "border-navy/10 bg-white opacity-60"
                        : overdue
                          ? "border-red-300 bg-red-50/60 ring-1 ring-red-200"
                          : "border-navy/10 bg-white"
                    }`}
                    data-testid={`reminder-card-${r.id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-display text-base font-bold text-navy" data-testid="reminder-name">{r.name}</h3>
                      <span className="rounded-full bg-skycyan/10 px-3 py-1 text-xs font-bold text-skycyan-600" data-testid="reminder-type">
                        {r.insurance_type}
                      </span>
                      {handled ? (
                        <span className="rounded-full bg-navy/10 px-3 py-1 text-xs font-bold text-charcoal/60" data-testid="reminder-days-left">
                          Handled
                        </span>
                      ) : overdue ? (
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-600" data-testid="reminder-days-left">
                          Overdue — win-back call
                        </span>
                      ) : (
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-bold ${
                            d <= 15 ? "bg-amber-50 text-amber-600" : "bg-mint-50 text-mint-700"
                          }`}
                          data-testid="reminder-days-left"
                        >
                          {d === 0 ? "Expires today" : `${d} days left`}
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-charcoal/60" data-testid="reminder-expiry">
                      {overdue && !handled ? "Expired " : "Expires "}
                      {new Date(r.expiry_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      {r.note ? ` · ${r.note}` : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a href={`tel:+91${r.phone.replace(/\D/g, "").slice(-10)}`} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-mint hover:text-mint-600" data-testid="reminder-call">
                        <Phone className="h-3.5 w-3.5" /> {r.phone}
                      </a>
                      {overdue && !handled && (
                        <button onClick={() => setReminderStatus(r.id, "handled")} className="flex items-center gap-1.5 rounded-full bg-mint px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-mint-600" data-testid="reminder-mark-handled">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Mark Handled
                        </button>
                      )}
                      {handled && (
                        <button onClick={() => setReminderStatus(r.id, "pending")} className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-navy-700" data-testid="reminder-mark-pending">
                          <RotateCcw className="h-3.5 w-3.5" /> Mark Pending
                        </button>
                      )}
                      <button onClick={() => removeReminder(r.id)} className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-50" data-testid="reminder-delete">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* GOOGLE BUSINESS PROFILE CHECKLIST */}
          <section data-testid="gbp-checklist-section">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy">
                <MapPin className="h-5 w-5 text-mint" />
              </span>
              <div>
                <h2 className="font-display text-xl font-extrabold tracking-tight text-navy" data-testid="gbp-heading">
                  Get Found on Google Maps
                </h2>
                <p className="text-xs text-charcoal/60">Tick off each step to list PocketBima for local searches.</p>
              </div>
              <span className="ml-auto rounded-full bg-mint-50 px-3 py-1 text-xs font-bold text-mint-700" data-testid="gbp-progress">
                {gbpDone.length}/{GBP_STEPS.length}
              </span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-navy/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-skycyan to-mint transition-all duration-500"
                style={{ width: `${(gbpDone.length / GBP_STEPS.length) * 100}%` }}
                data-testid="gbp-progress-bar"
              />
            </div>
            <div className="mt-5 space-y-2.5">
              {GBP_STEPS.map((step, i) => {
                const done = gbpDone.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleGbp(i)}
                    className={`flex w-full items-start gap-3.5 rounded-2xl border p-4 text-left transition-all duration-200 ${
                      done ? "border-mint/30 bg-mint-50" : "border-navy/10 bg-white hover:border-mint/40"
                    }`}
                    data-testid={`gbp-step-${i}`}
                  >
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                        done ? "border-mint bg-mint" : "border-navy/20"
                      }`}
                    >
                      {done && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className={`text-sm leading-relaxed ${done ? "text-charcoal/50" : "text-charcoal/90"}`}>
                      {step}
                    </span>
                  </button>
                );
              })}
            </div>
            <a
              href="https://www.google.com/business/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
              data-testid="gbp-open-link"
            >
              Open Google Business Profile <ExternalLink className="h-4 w-4" />
            </a>
          </section>

          {/* GOOGLE REVIEW REQUESTS */}
          <section data-testid="review-capture-section">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy">
                <Star className="h-5 w-5 text-mint" />
              </span>
              <div>
                <h2 className="font-display text-xl font-extrabold tracking-tight text-navy" data-testid="review-heading">
                  Google Review Requests
                </h2>
                <p className="text-xs text-charcoal/60">Text happy customers your review link after a sale.</p>
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-navy/10 bg-white p-5 shadow-soft">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-skycyan-600">Your Google review link</label>
              <div className="mt-2 flex gap-2">
                <input
                  value={reviewLink}
                  onChange={(e) => setReviewLink(e.target.value)}
                  placeholder="https://g.page/r/..."
                  className={inputCls}
                  data-testid="review-link-input"
                  aria-label="Google review link"
                />
                <button
                  onClick={saveLink}
                  disabled={linkSaving}
                  className="shrink-0 rounded-xl bg-navy px-5 text-xs font-bold text-white transition-colors hover:bg-navy-700 disabled:opacity-60"
                  data-testid="review-link-save"
                >
                  {linkSaving ? "Saving..." : "Save"}
                </button>
              </div>
              <div className="mt-5 grid gap-3">
                <input
                  placeholder="Customer name"
                  value={reviewName}
                  onChange={(e) => setReviewName(e.target.value)}
                  className={inputCls}
                  data-testid="review-name-input"
                  aria-label="Customer name"
                />
                <input
                  placeholder="Customer mobile number"
                  type="tel"
                  value={reviewPhone}
                  onChange={(e) => setReviewPhone(e.target.value)}
                  className={inputCls}
                  data-testid="review-phone-input"
                  aria-label="Customer mobile number"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => sendReview("sms")}
                    disabled={reviewLoading}
                    className="flex items-center justify-center gap-2 rounded-xl bg-mint py-3 text-xs font-bold text-white transition-colors hover:bg-mint-600 disabled:opacity-60"
                    data-testid="review-send-sms"
                  >
                    <Send className="h-3.5 w-3.5" /> Text via SMS
                  </button>
                  <button
                    onClick={() => sendReview("whatsapp")}
                    disabled={reviewLoading}
                    className="flex items-center justify-center gap-2 rounded-xl border-2 border-mint/40 py-3 text-xs font-bold text-mint-700 transition-colors hover:bg-mint-50 disabled:opacity-60"
                    data-testid="review-send-whatsapp"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </button>
                </div>
                {settings && !settings.sms_configured && (
                  <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs leading-relaxed text-amber-700" data-testid="sms-not-configured-note">
                    SMS isn't connected yet — WhatsApp works right now. Once your Twilio keys are
                    added, the SMS button will send automatically.
                  </p>
                )}
              </div>
            </div>
            {reviewRequests && reviewRequests.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="review-requests-list">
                {reviewRequests.slice(0, 5).map((r) => (
                  <div key={r.id} className="flex items-center gap-2.5 rounded-xl border border-navy/10 bg-white px-4 py-3 text-xs" data-testid={`review-request-${r.id}`}>
                    <span className="font-bold text-navy">{r.name}</span>
                    <span className="text-charcoal/50">{r.phone}</span>
                    <span className={`ml-auto rounded-full px-2.5 py-0.5 font-bold ${r.channel === "sms" ? "bg-skycyan/10 text-skycyan-600" : "bg-mint-50 text-mint-700"}`}>
                      {r.channel === "sms" ? "SMS" : "WhatsApp"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
